import type {ReactNode} from 'react';

import {SvgIconType} from 'common/components/svg-icon/types';

export enum InputType {
  Text = `text`,
  Password = `password`,
  Number = `number`,
  TextArea = `textarea`,
  Date = `date`,
  DatetimeLocal = `datetime-local`,
}

export enum InputSize {
  Small = `small`,
  Medium = `medium`,
  Large = `large`,
}

export enum InputTheme {
  Default = `default`,
  White = `white`,
}

export enum Necessary {
  Default = ``,
  Optional = `optional`,
  Recommended = `recommended`,
  Required = `required`,
}

export enum ValidationStatus {
  Default = ``,
  Valid = `valid`,
  Invalid = `invalid`,
}

export enum TextAlign {
  Start = `start`,
  Center = `center`,
  End = `end`,
}

export enum AutocompleteType {
  InviteUsersAutocompleteOff = `invite-users-autocomplete-off`,
  Off = `off`,
}

export type InputBlurEvent = React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>;
export type InputFocusEvent = React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>;
export type InputKeyboardEvent = React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>;
export type InputClearEvent = React.MouseEvent;
export type InputClickEvent = React.MouseEvent<HTMLInputElement> | React.MouseEvent<HTMLTextAreaElement>;
export type InputChangeEvent = React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>;
export type InputInputEvent = React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>;
export type InputClipboardEvent = React.ClipboardEvent<HTMLInputElement> | React.ClipboardEvent<HTMLTextAreaElement>;

export type InputProps = {
  /** Feedback for user to perform action when the input is focused. e.g. "Press enter to search" */
  actionText?: string;
  /** If set, action-text will always be visible, even when the input is empty */
  alwaysShowActionText?: boolean;
  /** Show clear input button regardless if there's input or not */
  alwaysShowClear?: boolean;
  /** enum: Object.values(AutocompleteType) */
  autocomplete?: AutocompleteType;
  ariaLabel?: string;
  /** If set, automatically focuses on the input field */
  autoFocus?: boolean;
  autoSelect?: boolean;
  className?: string;
  /** Whether input is disabled */
  disabled?: boolean;
  elref?: string;
  /** Error message to show when invalid validation status */
  errorMessage?: string;
  /** If set, we will show this string next to the label. This is useful to give the user additional context such as which SDK verion a field is supported in */
  extraInfo?: string;
  /** Id of input field, maps to field label for accessibility */
  fieldId?: string;
  /** Name of the input field, maps to field label */
  fieldName?: string;
  /** If set, an "info" icon will be shown next to the label. This value will be shown as a tooltip on hover */
  helpText?: string;
  /** Hide focus styling, typically because a parent is styling the focus state */
  hideFocusStyling?: boolean;
  hideRightIcon?: boolean;
  /** Left icon that goes inline with the input */
  icon?: SvgIconType | ``;
  /** Label on top that describes the input field */
  label?: string;
  max?: string; // use "string" to avoid default value for number type
  /** Maximum number of characters allowed */
  maxlength?: number;
  min?: string; // use "string" to avoid default value for number type
  /** Minimum width of the internal input element in pixels. This should be set when there's a prefixRenderer (which takes up horizontal space) to prevent the input element from shrinking below some minimum width. */
  minInputWidth?: number | null;
  multilinePrefix?: boolean;
  /** enum: Object.values(Necessary) */
  necessary?: string;
  notFocusable?: boolean;
  onBlur?: (ev: InputBlurEvent) => void;
  /**
   * Prefer to use `value`. Do NOT use `ev.target.value`. That value is a display
   * value and not the true backing value.
   * @param value
   * @param ev Do NOT use this for `ev.target.value`. Use the `value` params
   * @returns
   */
  onChange?: (value: string, ev: InputChangeEvent) => void;
  onClear?: (ev: InputClearEvent) => void;
  onClick?: (ev: InputClickEvent) => void;
  onFocus?: (ev: InputFocusEvent) => void;
  /**
   * Prefer to use `value`. Do NOT use `ev.target.value`. That value is a display
   * value and not the true backing value.
   * @param value
   * @param ev Do NOT use this for `ev.target.value`. Use the `value` params
   * @returns
   */
  onInput?: (value: string, ev: InputInputEvent) => void;
  onKeydown?: (ev: InputKeyboardEvent) => void;
  onKeyup?: (ev: InputKeyboardEvent) => void;
  onPaste?: (ev: InputClipboardEvent) => void;
  /** Placeholder text for input */
  placeholder?: string;
  /** renders content before the input element and after any icons */
  prefixRenderer?: () => ReactNode;
  /** Mono-width text at the beginning of the input */
  preText?: string;
  /** Whether input is readonly */
  readonly?: boolean;
  rightIcon?: SvgIconType;
  /** Used for fields where user will most likely replace the whole input value instead of editing portions of the input */
  selectOnFocus?: boolean;
  /** Show clear input button when there's no input */
  showClear?: boolean;
  showNumericInputControls?: boolean;
  size?: InputSize | `${InputSize}`; // Support string literals for backwards compatibility
  step?: string;
  style?: React.CSSProperties;
  /** renders content after the input element */
  suffixRenderer?: () => ReactNode;
  tabIndex?: number;
  textAlign?: TextAlign | `${TextAlign}`; // Support string literals for backwards compatibility
  theme?: InputTheme | `${InputTheme}`; // Support string literals for backwards compatibility
  type?: InputType | `${InputType}`; // Support string literals for backwards compatibility
  /** enum: Object.values(ValidationStatus) */
  validationStatus?: ValidationStatus | `${ValidationStatus}` | ``; // Support string literals for backwards compatibility
  value?: string;
};
